/**
 * printInLightMode — Wave 19 #100.
 *
 * Force-light print contract for the WHOLE current document.
 *
 * Some print flows render `window.print()` against the live DOM (instead of
 * spinning up a new tab via `printElement()`) — e.g. trip itineraries that
 * lay out across multiple sub-components, or research briefs printed
 * inline. For those flows, we still owe the user a LIGHT-MODE PDF.
 *
 * This helper:
 *   1. Records the current `data-theme` on `<html>`.
 *   2. Sets `data-theme="light"` so every `html[data-theme="light"] …`
 *      override in index.css applies to the live DOM during print.
 *   3. Adds a `hv-print-active` class for any extra print-only overrides.
 *   4. Calls `window.print()` and restores the original theme on the
 *      synchronous `afterprint` event (or a 600 ms fallback).
 *
 * The helper is idempotent — back-to-back invocations don't stack themes.
 *
 * CONTRACT: ALL `window.print()` calls in the app MUST go through this
 * helper. This is locked in `/app/memory/CONTRACTS.md` →
 * "Print Light Mode Contract".
 */
let _activePrintRestore = null;

export function printInLightMode({ delay = 80, onAfterPrint } = {}) {
  if (typeof window === "undefined") return;

  // If a previous print is still in flight, restore first.
  if (typeof _activePrintRestore === "function") {
    try {
      _activePrintRestore();
    } catch {
      /* noop */
    }
    _activePrintRestore = null;
  }

  const root = document.documentElement;
  const prevTheme = root.getAttribute("data-theme");
  const prevColorScheme = root.style.colorScheme;
  root.setAttribute("data-theme", "light");
  root.style.colorScheme = "light";
  document.body.classList.add("hv-print-active");

  let restored = false;
  const restore = () => {
    if (restored) return;
    restored = true;
    if (prevTheme) {
      root.setAttribute("data-theme", prevTheme);
    } else {
      root.removeAttribute("data-theme");
    }
    root.style.colorScheme = prevColorScheme || "";
    document.body.classList.remove("hv-print-active");
    window.removeEventListener("afterprint", onAfter);
    if (typeof onAfterPrint === "function") {
      try {
        onAfterPrint();
      } catch {
        /* noop */
      }
    }
    _activePrintRestore = null;
  };
  const onAfter = () => restore();
  _activePrintRestore = restore;

  window.addEventListener("afterprint", onAfter);

  // Give the browser one paint frame so the theme switch settles before
  // the print dialog snapshots the page.
  setTimeout(() => {
    try {
      window.print();
    } catch {
      /* noop */
    }
    // Some browsers (especially mobile Safari) don't fire `afterprint`
    // reliably — fall back to a timer.
    setTimeout(restore, 600);
  }, Math.max(0, delay));
}

export default printInLightMode;
